import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

import { apiFetch } from '@/lib/api-client';

/**
 * Whether the guarantee is still offered (capped server-side at
 * MONEY_BACK_CAP). False while loading or on error — never over-promise.
 */
export function useMoneyBackAvailable(): boolean {
  const { data } = useQuery({
    queryKey: ['public-offers'],
    queryFn: () => apiFetch<{ moneyBack: { available: boolean } }>('/public/offers'),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return data?.moneyBack?.available === true;
}

/**
 * 30-day money-back guarantee (terms §10 "refunds"). `pill` for hero/CTA rows,
 * `banner` for a prominent strip under plan grids. Keep wording in sync with
 * the terms clause and the demo agent's refund FAQ.
 */
export function GuaranteeBadge({ variant = 'pill', className = '' }: { variant?: 'pill' | 'banner'; className?: string }): JSX.Element | null {
  const available = useMoneyBackAvailable();
  if (!available) return null;
  if (variant === 'banner') {
    return (
      <div
        className={`mx-auto flex max-w-3xl items-start gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-left sm:items-center ${className}`}
      >
        <ShieldCheck className="h-8 w-8 flex-shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
        <div className="text-sm">
          <p className="font-semibold text-foreground">30-day money-back guarantee</p>
          <p className="mt-0.5 text-muted-foreground">
            Try it on real calls. Not happy within 30 days of your first payment? Email us for a full refund of your
            first month. No contract, cancel anytime.{' '}
            <Link to="/terms#refunds" className="underline underline-offset-4">
              Details
            </Link>
          </p>
        </div>
      </div>
    );
  }
  return (
    <Link
      to="/terms#refunds"
      className={`inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-500/15 dark:text-emerald-300 ${className}`}
    >
      <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
      30-day money-back guarantee
    </Link>
  );
}
